import React from "react";
import { SOCIAL_CONTENT_TYPE } from "../../Types/account";
import {
  MAX_CADENCE_STEPS,
  isSoloCadenceContentType,
  SOLO_CADENCE_CONTENT_TYPES,
} from "../../Api/requestSocialPrompt";
import { formatEnumValue } from "../../utils/formatters";

/** Local-only representation of a cadence step while editing. */
export type CadenceStepUi = {
  id: string;
  content_type: SOCIAL_CONTENT_TYPE;
  /** Sent as `objective` or null when trimmed empty. */
  objective: string;
};

/** Stable id for a local cadence step row. */
export function generateCadenceStepId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `cadence-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Short helper copy above the cadence step editor. */
export function getCadenceBuilderHint(options?: {
  excludeSoloChannels?: boolean;
  /** When true (cadences capability off), wording is for a single prompt — not a multi-step cadence. */
  singleStep?: boolean;
}): string {
  if (options?.singleStep) {
    return options.excludeSoloChannels
      ? "Pick a channel and optional objective for this prompt."
      : "Pick a channel and optional objective. LinkedIn posts and blogs are single-prompt only.";
  }
  const lead = "Add steps in the order you'll send them.";
  if (options?.excludeSoloChannels) {
    return `${lead} Pick a channel and optional objective for each step.`;
  }
  return `${lead} Email, call, and LinkedIn DM can combine in one sequence; LinkedIn posts and blogs must be solo.`;
}

/** A fresh, default cadence step (Email, no objective). */
export function createDefaultCadenceStep(): CadenceStepUi {
  return {
    id: generateCadenceStepId(),
    content_type: SOCIAL_CONTENT_TYPE.EMAIL,
    objective: "",
  };
}

type CadenceStepEditorProps = {
  steps: CadenceStepUi[];
  onChange: (next: CadenceStepUi[]) => void;
  /** Hide solo channels (e.g. contact-scoped requests). */
  excludeSoloChannels?: boolean;
  maxSteps?: number;
  /** Optional controls rendered to the right of the add-step button. */
  addStepRowActions?: React.ReactNode;
  /** Compact, visible channel choices for guided creation flows. */
  channelControl?: "select" | "pills";
};

/**
 * Shared editor for an ordered cadence (1–maxSteps). Enforces the rule that solo
 * channels (LinkedIn post, blog) must be the only step; other channels may combine.
 */
export const CadenceStepEditor: React.FC<CadenceStepEditorProps> = ({
  steps,
  onChange,
  excludeSoloChannels = false,
  maxSteps = MAX_CADENCE_STEPS,
  addStepRowActions,
  channelControl = "select",
}) => {
  const getContentTypeOptions = (): SOCIAL_CONTENT_TYPE[] =>
    Object.values(SOCIAL_CONTENT_TYPE).filter((type) => {
      if (excludeSoloChannels && SOLO_CADENCE_CONTENT_TYPES.has(type)) {
        return false;
      }
      if (steps.length > 1 && SOLO_CADENCE_CONTENT_TYPES.has(type)) {
        return false;
      }
      return true;
    });

  const handleTypeChange = (stepId: string, contentType: SOCIAL_CONTENT_TYPE) => {
    const ix = steps.findIndex((s) => s.id === stepId);
    if (ix === -1) return;
    if (isSoloCadenceContentType(contentType)) {
      const cur = steps[ix];
      onChange([
        {
          id: cur.id,
          content_type: contentType,
          objective: cur.objective,
        },
      ]);
      return;
    }
    const next = [...steps];
    next[ix] = { ...next[ix], content_type: contentType };
    onChange(next);
  };

  const handleObjectiveChange = (stepId: string, objective: string) => {
    onChange(steps.map((s) => (s.id === stepId ? { ...s, objective } : s)));
  };

  const handleAddStep = () => {
    if (steps.length >= maxSteps) return;
    if (steps.some((s) => isSoloCadenceContentType(s.content_type))) return;
    onChange([...steps, createDefaultCadenceStep()]);
  };

  const handleRemoveStep = (stepId: string) => {
    if (steps.length <= 1) return;
    onChange(steps.filter((s) => s.id !== stepId));
  };

  const canAddStep =
    steps.length < maxSteps &&
    !steps.some((s) => isSoloCadenceContentType(s.content_type));

  return (
    <>
      <div className="socialPromptModal_cadenceSteps">
        {steps.map((step, index) => {
          const typeOptions = getContentTypeOptions();
          const optionsForRow = typeOptions.includes(step.content_type)
            ? typeOptions
            : [...typeOptions, step.content_type];
          return (
            <div key={step.id} className="socialPromptModal_cadenceStepRow">
              {maxSteps > 1 ? (
                <div className="socialPromptModal_stepNumber" aria-hidden="true">
                  {index + 1}
                </div>
              ) : null}
              <div className="socialPromptModal_cadenceStepBody">
                <div className="socialPromptModal_cadenceStepTop">
                  <div className="socialPromptModal_cadenceStepChannel">
                    <label
                      className="socialPromptModal_cadenceFieldLabel"
                      htmlFor={`cadence-type-${step.id}`}
                    >
                      Channel
                    </label>
                    {channelControl === "pills" ? (
                      <div
                        id={`cadence-type-${step.id}`}
                        className="socialPromptModal_cadenceChannelPills"
                        role="group"
                        aria-label={maxSteps > 1 ? `Step ${index + 1} channel` : "Channel"}
                      >
                        {optionsForRow.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            className={[
                              "socialPromptModal_cadenceChannelPill",
                              step.content_type === opt
                                ? "socialPromptModal_cadenceChannelPill--active"
                                : "",
                            ].filter(Boolean).join(" ")}
                            aria-pressed={step.content_type === opt}
                            onClick={() => handleTypeChange(step.id, opt)}
                          >
                            {formatEnumValue(opt)}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <select
                        id={`cadence-type-${step.id}`}
                        className="socialPromptModal_cadenceTypeSelect"
                        aria-label={
                          maxSteps > 1 ? `Step ${index + 1} channel` : "Channel"
                        }
                        value={step.content_type}
                        onChange={(e) =>
                          handleTypeChange(step.id, e.target.value as SOCIAL_CONTENT_TYPE)
                        }
                      >
                        {optionsForRow.map((opt) => (
                          <option key={opt} value={opt}>
                            {formatEnumValue(opt)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {steps.length > 1 && (
                    <button
                      type="button"
                      className="socialPromptModal_removeStepButton"
                      onClick={() => handleRemoveStep(step.id)}
                      aria-label={`Remove step ${index + 1}`}
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  )}
                </div>
                <div className="socialPromptModal_cadenceObjective">
                  <label
                    className="socialPromptModal_cadenceFieldLabel"
                    htmlFor={`cadence-obj-${step.id}`}
                  >
                    Objective
                    {channelControl === "pills" ? (
                      <span className="socialPromptModal_cadenceOptionalLabel">Optional</span>
                    ) : (
                      " (optional)"
                    )}
                  </label>
                  <textarea
                    id={`cadence-obj-${step.id}`}
                    className="socialPromptModal_objectiveInput"
                    rows={2}
                    placeholder={
                      maxSteps > 1
                        ? "Planner hint for this step…"
                        : "Optional guidance for this prompt…"
                    }
                    value={step.objective}
                    onChange={(e) => handleObjectiveChange(step.id, e.target.value)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {(canAddStep || addStepRowActions) && (
        <div className="socialPromptModal_addStepRow">
          {canAddStep && (
            <button
              type="button"
              className="socialPromptModal_addStepButton"
              onClick={handleAddStep}
              aria-label="Add cadence step"
              title="Add cadence step"
            >
              <i className="fa-solid fa-plus" aria-hidden />
              <span className="socialPromptModal_addStepButtonText">Add step</span>
            </button>
          )}
          {addStepRowActions && (
            <div className="socialPromptModal_addStepRowActions">{addStepRowActions}</div>
          )}
        </div>
      )}
    </>
  );
};
